import type {
  ExtendedPaymentInfoFormData,
  PaymentInfoProps,
} from '@openeducation/pp-rn-shared-types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CARD_HOLDER_NAME_MIN_LENGTH = 3;

interface UsePaymentInfo {
  formData: ExtendedPaymentInfoFormData;
  validation: PaymentInfoProps['validation'];
  totalAmount?: number;
}

export const usePaymentInfo = ({
  formData,
  validation,
  totalAmount,
}: UsePaymentInfo) => {
  const { t } = useTranslation();
  const [focusedField, setFocusedField] = useState<string>();

  const card1TypeError = useMemo(() => {
    if (!formData.card1_cardType) {
      return t('input.card_type.error.required');
    }
    return validation.isCard1TypeValid ? '' : t('input.card_type.error.valid');
  }, [formData.card1_cardType, validation.isCard1TypeValid, t]);

  const card1CardNumberError = useMemo(() => {
    if (!formData.card1_cardNumber) {
      return t('input.card_number.error.required');
    }
    return validation.isCard1NumberValid
      ? ''
      : t('input.card_number.error.valid');
  }, [formData.card1_cardNumber, validation.isCard1NumberValid, t]);

  const card1ExpirationDateError = useMemo(() => {
    if (!formData.card1_expirationMonth && !formData.card1_expirationYear) {
      return t('input.card_expiration.error.required');
    }
    if (!formData.card1_expirationMonth || !formData.card1_expirationYear) {
      return t('input.card_expiration.error.valid');
    }
    return validation.isCard1ExpirationValid
      ? ''
      : t('input.card_expiration.error.tooold');
  }, [
    formData.card1_expirationMonth,
    formData.card1_expirationYear,
    validation.isCard1ExpirationValid,
    t,
  ]);

  const card1HolderNameError = useMemo(() => {
    if (!formData.card1_holderName) {
      return t('input.card_name.error.required');
    }
    if (formData.card1_holderName.trim().length < CARD_HOLDER_NAME_MIN_LENGTH) {
      return t('input.card_name.error.minlength');
    }
    return validation.isCard1HolderNameValid
      ? ''
      : t('input.card_name.error.valid');
  }, [formData.card1_holderName, validation.isCard1HolderNameValid, t]);

  const card1CvvError = useMemo(() => {
    if (!formData.card1_cvv) {
      return t('input.card_code.error.required');
    }
    return validation.isCard1CvvValid
      ? ''
      : t('error.creditCard.securityCode.invalid');
  }, [formData.card1_cvv, validation.isCard1CvvValid, t]);

  const card2TypeError = useMemo(() => {
    if (!formData.card2_cardType) {
      return t('input.card_type.error.required');
    }
    return validation.isCard2TypeValid ? '' : t('input.card_type.error.valid');
  }, [formData.card2_cardType, validation.isCard2TypeValid, t]);

  const card2CardNumberError = useMemo(() => {
    if (!formData.card2_cardNumber) {
      return t('input.card_number.error.required');
    }
    return validation.isCard2NumberValid
      ? ''
      : t('input.card_number.error.valid');
  }, [formData.card2_cardNumber, validation.isCard2NumberValid, t]);

  const card2ExpirationDateError = useMemo(() => {
    if (!formData.card2_expirationMonth && !formData.card2_expirationYear) {
      return t('input.card_expiration.error.required');
    }
    if (!formData.card2_expirationMonth || !formData.card2_expirationYear) {
      return t('input.card_expiration.error.valid');
    }
    return validation.isCard2ExpirationValid
      ? ''
      : t('input.card_expiration.error.tooold');
  }, [
    formData.card2_expirationMonth,
    formData.card2_expirationYear,
    validation.isCard2ExpirationValid,
    t,
  ]);

  const card2HolderNameError = useMemo(() => {
    if (!formData.card2_holderName) {
      return t('input.card_name.error.required');
    }
    if (formData.card2_holderName.trim().length < CARD_HOLDER_NAME_MIN_LENGTH) {
      return t('input.card_name.error.minlength');
    }
    return validation.isCard2HolderNameValid
      ? ''
      : t('input.card_name.error.valid');
  }, [formData.card2_holderName, validation.isCard2HolderNameValid, t]);

  const card2CvvError = useMemo(() => {
    if (!formData.card2_cvv) {
      return t('input.card_code.error.required');
    }
    return validation.isCard2CvvValid
      ? ''
      : t('error.creditCard.securityCode.invalid');
  }, [formData.card2_cvv, validation.isCard2CvvValid, t]);

  const card1AmountError = useMemo(() => {
    if (!formData.card1_amount) {
      return t('input.card_amount.error.required');
    }

    const amount = parseFloat(formData.card1_amount) || 0;

    if (totalAmount && amount >= totalAmount) {
      return t('input.card_amount.error.max_amount_exceeded');
    }

    return validation.isCard1AmountValid
      ? ''
      : t('input.card_amount.error.min_percentage');
  }, [formData.card1_amount, validation.isCard1AmountValid, totalAmount, t]);

  const handleFocus = useCallback(
    (name?: string) => name && setFocusedField(name),
    [setFocusedField]
  );

  const handleBlur = useCallback(
    () => setFocusedField(undefined),
    [setFocusedField]
  );

  const computeCardExpiryDate = useCallback(
    (
      monthKey: keyof ExtendedPaymentInfoFormData,
      yearKey: keyof ExtendedPaymentInfoFormData
    ) => {
      const month = formData[monthKey] as string;
      const year = formData[yearKey] as string;
      if (!month || !year) {
        return '';
      }
      return `${month.padStart(2, '0')}/${parseInt(year, 10) + 2000}`;
    },
    [formData]
  );

  const card1ExpiryDate = useMemo(
    () =>
      computeCardExpiryDate('card1_expirationMonth', 'card1_expirationYear'),
    [
      formData.card1_expirationMonth,
      formData.card1_expirationYear,
      computeCardExpiryDate,
    ]
  );

  const card2ExpiryDate = useMemo(
    () =>
      computeCardExpiryDate('card2_expirationMonth', 'card2_expirationYear'),
    [
      formData.card2_expirationMonth,
      formData.card2_expirationYear,
      computeCardExpiryDate,
    ]
  );

  const focusedCardFieldType = useMemo(() => {
    if (!focusedField) {
      return undefined;
    }
    const fieldName = focusedField.substring(focusedField.indexOf('_') + 1);
    switch (fieldName) {
      case 'cardNumber':
        return 'number';
      case 'expirationMonth':
      case 'expirationYear':
        return 'expiry';
      case 'holderName':
        return 'name';
      case 'cvv':
        return 'cvc';
      default:
        return undefined;
    }
  }, [focusedField]);

  return {
    card1TypeError,
    card1CardNumberError,
    card1ExpirationDateError,
    card1HolderNameError,
    card1CvvError,
    card1AmountError,
    card1ExpiryDate,
    card2TypeError,
    card2CardNumberError,
    card2ExpirationDateError,
    card2HolderNameError,
    card2CvvError,
    card2ExpiryDate,
    handleFocus,
    handleBlur,
    focusedCardFieldType,
  };
};
